import React, { useEffect, useState } from 'react';
import axios from 'axios';
import parse from 'html-react-parser';
import { Html } from 'react-pdf-html';
import {
   PDFDownloadLink,
   PDFViewer,
   Document,
   Page,
   Image,
   Text,
   View,
   StyleSheet,
   Link,
   Font,
} from '@react-pdf/renderer';
import './App.css';

function App() {
   const [products, setProducts] = useState([]);
   const [settings, setSettings] = useState(window.GMWCP_PDF_DATA);
   const [isLoading, setIsLoading] = useState(true);
   const [isMobile, setIsMobile] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const [totalPages, setTotalPages] = useState(1);
   const [siteUrl, setSiteUrl] = useState(window.GMWCP_PDF_DATA['site_url']);
   const [pluginUrl, setPluginUrl] = useState(window.GMWCP_PDF_DATA['pluginurl']); 
   const [restapiUrl, setRestApiUrl] = useState(window.GMWCP_PDF_DATA['rest_api_url']); 
   const [rest_api_product, setRestApiProductUrl] = useState(window.GMWCP_PDF_DATA['rest_api_product']);  
   const [error, setError] = useState('');

   useEffect(() => {
      if (!siteUrl) return; // Exit early if siteUrl is empty

      Font.register({
         family: 'Roboto',
         fonts: [
            {
               src: `${pluginUrl}/build/fonts/Roboto-Regular.ttf`,
               fontWeight: 'normal',
            },
            {
               src: `${pluginUrl}/build/fonts/Roboto-Bold.ttf`,
               fontWeight: 'bold',
            },
            
         ],
      });
   }, [siteUrl]);

   useEffect(() => {
      if (!siteUrl) return; // Exit early if siteUrl is empty

      setIsMobile(/Mobi|Android/i.test(navigator.userAgent));

      const fetchProducts = async () => {
         try {
            let productsApiUrl = `${rest_api_product}`;
            let currentPage = 1;
            let allProducts = [];
            let totalPages = 1;

            while (currentPage <= totalPages) {
               const separator = productsApiUrl.includes('?') ? '&' : '?';
               let response;
               response = await axios.get(`${productsApiUrl}${separator}page=${currentPage}`);

               allProducts = [...allProducts, ...response.data];
               totalPages = response.headers['x-wp-totalpages'];
               setCurrentPage(currentPage);
               setTotalPages(totalPages);
               currentPage++;
            }

            setProducts(allProducts);
         } catch (error) {
            console.error('Error fetching products:', error);
            setError('Error fetching products');
         } finally {
            setIsLoading(false);
         }
      };
      fetchProducts();
   }, [siteUrl]);

   // Chunk the array based on dynamic page break value
   const chunkArray = (array, size) => {
      const chunkedArr = [];
      for (let i = 0; i < array.length; i += size) {
         chunkedArr.push(array.slice(i, i + size));
      }
      return chunkedArr;
   };

   const ProductViewPdf = ({ products }) => {
      const styles = StyleSheet.create({
         body: {
            padding: 0,
            fontSize: 12,
            fontFamily: 'Roboto',
         },
         header: {
            fontSize: 12,
            marginBottom: 0,
            textAlign: 'center',
            paddingBottom: 10,
            backgroundColor: settings.setting?.gmpcp_hf_background_color || '#0084B4',
            color: settings.setting?.gmpcp_hf_item_background_color || '#f5f5f5',
            padding: 5,
         },
         galleryImagesContainer: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 10,
            marginTop: 2,
            flexWrap: 'wrap',
            gap: 0,
         },
         galleryImage: {
            width: '33%',
         },
         footer: {
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 12,
            borderTop: '1px solid #ccc',
            padding: 5,
            backgroundColor: settings.setting?.gmpcp_hf_background_color || '#f5f5f5',
            color: settings.setting?.gmpcp_hf_item_background_color || '#f5f5f5',
         },
         contentSection: {
            padding: 10,
            gap: 10,
         },
         contentContainermain:{
            backgroundColor: settings.setting?.gmpcp_background_color || '#f5f5f5',
            color: settings.setting?.gmpcp_item_background_color || '#f5f5f5',
            fontSize:10
         },
         contentContainer: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            padding: 10,
            gap: 10,
         },
         leftContent: {
            width: (settings.setting?.gmpcp_image_width || '40')+'%',
         },
         rightContent: {
            width: (100-settings.setting?.gmpcp_image_width || '60')+'%',
         },
         productName: {
            fontSize: 16,
            fontWeight: 'bold',
            marginBottom: 10,
         },
         productLink: {
            marginTop: 10,
            color: 'blue',
            textDecoration: 'underline',
         },
         boldLabel: {
            fontWeight: 'bold',
            color: '#000',
         },
         contentpart: {
            marginTop: 5,
         },
      });

      if (error) {
         return (
            <Document>
               <Page size="A4">
                  <View style={styles.body}>
                     <Text>Error: {error}</Text>
                  </View>
               </Page>
            </Document>
         );
      }

      if (!products || products.length === 0 || !settings) return null;

      const pageBreak = parseInt(settings.setting?.gmpcp_pagebreak, 10) || 1;
      const productChunks = chunkArray(products, pageBreak);
      console.log(productChunks);
      return (
         <Document>
            {productChunks.map((productChunk, chunkIndex) => (
               <Page key={chunkIndex} size="A4" style={styles.body}>
                 
                  <View style={styles.header}>
                     <Text>{settings.setting?.gmpcp_header_text || ''}</Text>
                  </View>
                  
                  {/* Render products directly in the JSX without a separate function */}
                  {productChunk.map((product, index) => (
                     <View key={index} style={styles.contentContainermain}>
                        <View style={styles.contentContainer}>
                           <View style={styles.leftContent}>
                              {settings.setting?.gmwcp_show_hide?.includes('images') &&
                                 product.images && product.images.full && (
                                    <Image
                                       src={product.images.full}
                                       alt={product.name}
                                       style={styles.image}
                                    />
                                 )}
                              {settings.setting?.gmwcp_show_hide?.includes('gallery') && (
                                 <View style={styles.galleryImagesContainer}>
                                    {product.gallery_images?.map((image, imgIndex) => (
                                       <Image
                                          key={imgIndex}
                                          src={image.src}
                                          alt={image.alt}
                                          style={styles.galleryImage}
                                       />
                                    ))}
                                 </View>
                              )}
                           </View>

                           <View style={styles.rightContent}>
                              {settings.setting?.gmwcp_show_hide?.includes('title') && (
                                 <Text style={styles.productName}>{product.name}</Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('short_desc') && (
                                 <Text>{parse(product.short_description)}</Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('read_more') && (
                                 <Link src={product.permalink} style={styles.productLink}>
                                    {settings.translation?.gmpcp_trasnlation_read_more?.val}
                                 </Link>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('sku') && (
                                 <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                       {settings.translation?.gmpcp_trasnlation_sku?.val}{' '}
                                    </Text>
                                    {product.sku}
                                 </Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('price') && (
                                 <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                       {settings.translation?.gmpcp_trasnlation_price?.val}{' '}
                                    </Text>
                                    {product.price}
                                 </Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('stock_status') && (
                                 <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                       {settings.translation?.gmpcp_trasnlation_stock?.val}{' '}
                                    </Text>
                                    {product.stock_status}
                                 </Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('categories') && (
                                 <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                       {settings.translation?.gmpcp_trasnlation_categories?.val}{' '}
                                    </Text>
                                    {product.producat_cat.join(', ')}
                                 </Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('tags') && (
                                 <Text style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>
                                       {settings.translation?.gmpcp_trasnlation_tags?.val}{' '}
                                    </Text>
                                    {product.producat_tag.join(', ')}
                                 </Text>
                              )}
                              {settings.setting?.gmwcp_show_hide?.includes('attributes') && (
                                 <View style={styles.contentpart}>
                                    <Text style={styles.boldLabel}>Attribute:</Text>
                                    {product.attributes && typeof product.attributes === 'object' && !Array.isArray(product.attributes) ? (
                                      Object.entries(product.attributes).map(([key, value], index) => (
                                        <Text key={index}>
                                          <Text style={styles.boldLabel}>{key}</Text>: {value}
                                        </Text>
                                      ))
                                    ) : (
                                      product.attributes.map((field, index) => (
                                        <Text key={index}>
                                         <Text style={styles.boldLabel}>{field.key}</Text> : {field.value}
                                        </Text>
                                      ))
                                    )}

                                 </View>
                              )}
                           </View>
                        </View>
                        {settings.setting?.gmwcp_show_hide?.includes('long_desc') && (
                        <View style={styles.contentContainer}>
                           <Html>
                              {`
                                 <style>
                                 h1,h2,h3,h4,h5,h6{
                                    margin:0;
                                    font-size:12px;
                                    margin-bottom:2px;
                                 }
                                 </style>
                                 <div style="font-size:12px; color: #000;">
                                    ${product.description}
                                 </div>
                              `}
                           </Html>
                        </View>
                         )}
                     </View>
                  ))}
                 
                  <View style={styles.footer}>
                     <Text>{settings.setting?.gmpcp_footer_text || ''}</Text>
                  </View>
               </Page>
            ))}
         </Document>
      );
   };

   return (
      <div className="App">
         {isLoading ? (
            <div>Loading products...</div>
         ) : (
            <PDFViewer style={{ width: '100%', height: '100vh' }}>
               <ProductViewPdf products={products} />
            </PDFViewer>
         )}
      </div>
   );
}

export default App;
